import {TemplateNode} from "#template/template-node/template-node"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {OutputNodeParameters} from "#template/fundamental/output/types"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {EvaluatedTemplateOutputs} from "#template/fundamental/evaluated-input"
import {Unresolved} from "#template/runtime-graph/types"
import {fundamentalLikeMarker, type FundamentalLikeNode} from "#template/role-markers"
import {outputKeyFromOutputTypeAndOutputId, wrapInterfaceId} from "#template/interface-descriptors"
import {getAffectedTemplateNodeParts} from "#template/fundamental/helpers"
import {visitNone} from "#template/template-node/visit-helpers"
import type {OnCompileContext, VisitorNodeResult, TemplateNodeContext} from "#template/template-node/types"
import type {Evaluator} from "@cm/graph-v3"

export abstract class OutputNode<out ItemType = unknown> extends TemplateNode<OutputNodeParameters<ItemType>> implements FundamentalLikeNode<ItemType | null> {
    get [fundamentalLikeMarker]() {
        return true as const
    }

    abstract get ancestry(): readonly string[]

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ItemType | null> {
        return scope.pureLambda(
            scope.tuple(
                evaluator.templateScope.resolve<EvaluatedTemplateOutputs>(`templateOutputs-${evaluator.getLocalId(this.parameters.template)}`),
                this.ancestry[0],
                this.parameters.outputId,
            ),
            ([outputs, type, outputId]) => {
                if (outputs === Unresolved) return null
                return (outputs[outputKeyFromOutputTypeAndOutputId(type, outputId)] ?? null) as ItemType | null
            },
            `output${this.instanceId}`,
        )
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<OutputNodeParameters<ItemType>> {
        const {evaluator, currentTemplate, path} = context
        const {activeNodeSet} = evaluator

        const {parts, affectedOutputIds: reExportIds} = getAffectedTemplateNodeParts(this, activeNodeSet, (node) => node instanceof OutputNode)
        const affectedObjectPathParts = parts.map(({templateNode: n, part}) => ({path: [...path, n], part}))

        const key = wrapInterfaceId(this.parameters.template.parameters.id, this.parameters.outputId)
        const existing = currentTemplate.outputAffectedObjectPathParts.get(key) ?? {parts: [], reExportIds: []}
        currentTemplate.outputAffectedObjectPathParts.set(key, {
            parts: [...existing.parts, ...affectedObjectPathParts],
            reExportIds: [...existing.reExportIds, ...reExportIds],
        })

        return visitNone(this.parameters)
    }
}
